import {
    DollarSign,
    Package,
    ArrowUpRight,
    Activity,
    Eye,
    Truck, 
    XCircle, 
    CheckCircle, 
    FilePlus2,
    PackageCheck,
    HelpCircle,
    Undo2,
    Ban,
    Copy,
    User,
    Wallet,
    Headset,
    RefreshCcw
} from "lucide-react"
import { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom"; 
import { useAuth } from "../../../context/AuthProvider";
import { BASE_URL } from '../../../apiConfig';
import Loading from "../../../cors/Loading";
import { toast } from 'sonner';
import BaarChart from "../../../ui/BaarChart";
import LinneChart from "../../../ui/LinneChart";
import RadialChart from "../../../ui/RadialChart";
import SelectDate from "../../../cors/SelectDate";
import Map from "../../../cors/Map";

export default function DashboardSaller() {
  const { token } = useAuth();
  
  const [state, setState] = useState({
    total_revenue: 0,
    total_profit_sellers: 0,
    total_profit_confirmator: 0,
    total_profit: 0,
    total_orders: 0,
    orders_status: {},
    top_products: [],
    top_sellers: [],
    last_payments: [],
  });
  const [loading, setLoading] = useState(true);
  const [showContent, setShowContent] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  
  const [currentRange, setCurrentRange] = useState(null);
  const currentRangeRef = useRef(null);

  const handleDateRangeChange = useCallback(async (range) => {
    setLoading(true);
    setShowContent(false);
    setCurrentRange(range);
    currentRangeRef.current = range; 
    await fetchStats(range);
  }, []);

  const fetchStats = useCallback(async (range = null) => {
    try {
      const requestBody = { range };

      const response = await fetch(BASE_URL + '/api/dashboard-stats', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(requestBody)
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`API Error: ${response.status} - ${errorText}`);
        throw new Error(`Failed to fetch dashboard stats: ${response.status}`);
      }
      
      const result = await response.json();
      
      if (result.code === 200 && result.data) {
        setState(result.data);
        setTimeout(() => setShowContent(true), 100);
      } else {
        console.error("Unexpected API response structure:", result);
        toast.error("Unexpected data format received from API.");
        setShowContent(true); 
      }
    } catch (err) {
      console.error("Fetch error:", err);
      toast.error(err.message || "Error loading dashboard statistics.");
      setShowContent(true);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [token]);

  const handleRefresh = useCallback(async () => {
    if (loading || refreshing) return; 
    setRefreshing(true);
    const rangeToUse = currentRangeRef.current;
    await fetchStats(rangeToUse);
  }, [loading, refreshing, fetchStats]);

  useEffect(() => {
    currentRangeRef.current = currentRange;
  }, [currentRange]);

  const getStatusColor = (status) => {
    switch (status) {
      case "nouveau":
        return "bg-blue-100 text-blue-700 border border-blue-200";
      case "confirme":
        return "bg-green-100 text-green-700 border border-green-200";
      case "suivi":
        return "bg-amber-100 text-amber-700 border border-amber-200";
      case "reporte":
        return "bg-yellow-100 text-yellow-700 border border-yellow-200";
      case "indelivery":
        return "bg-indigo-100 text-indigo-700 border border-indigo-200";
      case "livre":
        return "bg-emerald-100 text-emerald-700 border border-emerald-200";
      case "retour":
        return "bg-orange-100 text-orange-700 border border-orange-200";
      case "refuse":
        return "bg-red-100 text-red-700 border border-red-200";
      case "annule":
        return "bg-gray-200 text-gray-700 border border-gray-300";
      case "duplicated":
        return "bg-pink-100 text-pink-700 border border-pink-200";
      default:
        return "bg-gray-100 text-gray-700 border border-gray-200";
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case "nouveau":
        return "Nouvelle";
      case "confirme":
        return "Confirmé";
      case "reporte":
        return "Attente de confirmation";
      case "suivi":
        return "En suivi";
      case "indelivery":
        return "En livraison";
      case "livre":
        return "Livré";
      case "retour":
        return "Returné";
      case "refuse":
        return "Refusé";
      case "annule":
        return "Annulé";
      case "duplicated":
        return "Dupliqué";
      default:
        return status;
    }
  };

  const getStatusIcon = (status) => {
    const iconSize = 40;
    switch (status) {
      case "nouveau":
        return <FilePlus2 size={iconSize} />;
      case "confirme":
        return <CheckCircle size={iconSize} />;
      case "suivi":
        return <Headset size={iconSize} />;
      case "reporte":
        return <HelpCircle size={iconSize} />;
      case "indelivery":
        return <Truck size={iconSize} />;
      case "livre":
        return <PackageCheck size={iconSize} />;
      case "retour":
        return <Undo2 size={iconSize} />;
      case "refuse":
        return <XCircle size={iconSize} />;
      case "annule":
        return <Ban size={iconSize} />;
      case "duplicated":
        return <Copy size={iconSize} />;
      default:
        return null;
    }
  }
  
  function formatCount(amount) {
    return new Intl.NumberFormat('en-US', {
    }).format(amount || 0);
  }

  function formatMoney(amount) {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  }

  const stats = [
    {
      title: "Chiffre d'affaires",
      value: `${formatMoney(state?.total_revenue)} MAD`,
      initialValue: "0.00 MAD",
      change: "+20.1%", 
      icon: DollarSign,
      color: "bg-green-500",
    },
    {
      title: "Total d'achat",
      value: `${formatMoney(state?.total_saller_price)} MAD`,
      initialValue: "0.00 MAD",
      change: "+2.1%",
      icon: User, 
      color: "bg-orange-500",
    },
    {
      title: "Frais de livraison",
      value: `${formatMoney(state?.total_delivrey_price)} MAD`,
      initialValue: "0.00 MAD",
      change: "+2.1%",
      icon: Headset, 
      color: "bg-yellow-500",
    },
    {
      title: "Profit Net",
      value: ` ${formatMoney(state?.total_profit)} MAD`,
      initialValue: "0.00 MAD",
      change: "+2.1%",
      icon: Wallet, 
      color: "bg-teal-500",
    },
  ];

  const topProducts = (state.top_products?.length > 0 ? state.top_products : Array(5).fill({
    name: "Chargement...",
    image_py: "",
    total_quantity: "0",
    total_revenue: "0.00"
  })).map(product => ({
    name: product.name,
    image: product.image_py,
    sales: parseInt(product.total_quantity) || 0,
    revenue: `${formatMoney(product.total_revenue)} MAD`,
    progress: Math.min(85, ((parseInt(product.total_quantity) || 0) / (state?.total_orders || 1)) * 100),
  }));

  return (
    <div className="min-h-screen">
      <div className="flex">
        <main className="flex-1 md:ml-0">
          <div className="py-8">
            <div className="mx-auto px-4 sm:px-6 px-5">
              <div className={`mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between transition-all duration-200 `}>
                <div>
                  <h1 className="text-4xl max-md:text-2xl font-bold bg-gradient-to-r from-[var(--color-primary-500)] to-[var(--color-primary-400)] bg-clip-text text-transparent mb-4">
                    Tableau de bord
                  </h1>
                  <p className="text-sm max-xl:text-xs text-gray-600 max-w-2xl">
                    Bon retour ! Voici ce qui se passe dans votre entreprise.
                  </p>
                </div>
                <div className="flex max-xl:grid items-center gap-3 max-lg:mt-10">
                  <SelectDate onDateRangeChange={handleDateRangeChange} />
                  <button 
                    className={`flex items-center max-w-max gap-2 rounded-xl bg-white shadow-sm py-2.5 px-4 cursor-pointer text-xs transition-all duration-200 ${
                      loading || refreshing 
                        ? 'opacity-50 cursor-not-allowed' 
                        : 'hover:bg-gray-50 hover:shadow-md'
                    }`}
                    onClick={handleRefresh}
                    disabled={loading || refreshing}
                  >
                    <RefreshCcw 
                      className={`w-3.5 h-3.5 transition-transform duration-500 ease-in-out ${
                        refreshing ? 'animate-spin' : ''
                      }`} 
                    />
                    <span>
                      {refreshing ? 'Actualisation...' : 
                       loading ? 'Chargement...' : 'Actualiser'}
                    </span>
                  </button>
                </div>
              </div>

              <div className={`grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-4 mb-8 transition-all duration-1000 `}>
                {stats.map((stat, index) => (
                  <div
                    key={stat.title}
                    className={`bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-500 p-6 `}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-600 mb-1">{stat.title}</p>
                        <p className="text-2xl font-bold text-gray-900 transition-all duration-700">
                          {showContent ? stat.value : stat.initialValue}
                        </p>
                        <div className="flex items-center mt-2">
                          <ArrowUpRight className="h-4 w-4 text-green-500 mr-1" />
                          <span className="text-sm font-medium text-green-600">{stat.change}</span>
                          <span className="text-sm text-gray-500 ml-1">vs last month</span>
                        </div>
                      </div>
                      <div className={`${stat.color} p-3 rounded-xl shadow-lg transition-all duration-500`}>
                        <stat.icon className="h-6 w-6 text-white" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
                <div>
                  {
                    loading ?
                    <div className="flex justify-center mt-52">
                      <Loading /> 
                    </div>
                    :
                    <div>
                      <div className={`mb-10 grid grid-cols-5 max-lg:grid-cols-2 gap-5 transition-all duration-700 delay-300 ${
                        showContent ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                      }`}>
                      
                        <Link
                          to="/orders/tout"
                          className={`
                            relative overflow-hidden rounded-xl
                            bg-emerald-100 border border-emerald-200
                            text-emerald-500 shadow-md
                            transition-all duration-300
                            hover:shadow-xl hover:scale-[1.03]
                            ${showContent ? "scale-100" : "scale-95"}
                          `}
                        >
                          <svg
                              className="absolute inset-0 w-full h-full opacity-30"
                              viewBox="0 0 400 200"
                              fill="none"
                            >
                              <path
                                d="M280 0 C340 40 360 80 400 120"
                                stroke="#666"
                                strokeDasharray="4 6"
                                strokeWidth="1"
                              />
                              <path
                                d="M120 200 C160 140 220 100 300 80"
                                stroke="#666"
                                strokeDasharray="4 6"
                                strokeWidth="1"
                              />
                            </svg>

                          <div className="relative px-5 py-2 flex flex-col justify-between h-28">
                            <div className="flex items-center justify-between">
                              <div className="text-sm font-medium opacity-90">
                                Tout
                              </div>

                              <Package size={40} className="opacity-90" />
                            </div>

                            <div className="text-2xl font-semibold">
                              {formatCount(state?.total_orders) || 0}
                            </div>
                          </div>
                        </Link>

                        {   
                          Object.entries(state?.orders_status || {}).map(([statusName, count], index) => (
                            <Link
                                key={index}
                                to={`/orders/${statusName}`}
                                className={`
                                  relative overflow-hidden rounded-xl
                                  shadow-md
                                  transition-all duration-300
                                  hover:shadow-xl hover:scale-[1.03]
                                  ${getStatusColor(statusName)}
                                  ${showContent ? "scale-100" : "scale-95"}
                                `}
                              >
                                <svg
                              className="absolute inset-0 w-full h-full opacity-30"
                              viewBox="0 0 400 200"
                              fill="none"
                            >
                              <path
                                d="M280 0 C340 40 360 80 400 120"
                                stroke="#666"
                                strokeDasharray="4 6"
                                strokeWidth="1"
                              />
                              <path
                                d="M120 200 C160 140 220 100 300 80"
                                stroke="#666"
                                strokeDasharray="4 6"
                                strokeWidth="1"
                              />
                            </svg>

                                <div className="relative px-5 py-2 flex flex-col justify-between h-28">
                                  <div className="flex items-center justify-between">
                                    <div className="text-sm w-20 font-medium opacity-90">
                                      {getStatusText(statusName)}
                                    </div>

                                    {getStatusIcon(statusName)}
                                  </div>

                                  <div className="text-2xl font-semibold">
                                    {formatCount(count)}
                                  </div>
                                </div>
                              </Link>
                            
                          ))
                        }
                      </div>  

                      <div className={`grid gap-8 grid-cols-1 xl:grid-cols-5 transition-all duration-700 delay-500 ${
                        showContent ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                      }`}>
                        <div className="xl:col-span-3 space-y-6 ">
                          <div className="w-full grid grid-cols-2 max-[1450px]:grid-cols-2 max-sm:grid-cols-1  items-center gap-6">
                            <div className="w-[100%] bg-white rounded-xl shadow-lg transition-all duration-500 hover:shadow-xl">
                              <div className="p-4 border-b border-gray-200">
                                <div className="flex items-center justify-between">
                                  <div>
                                    <h3 className="text-sm font-bold text-gray-900">Pourcentage Livré</h3>
                                    <p className="text-[10px] text-gray-600">Indique des commandes livrées.</p>
                                  </div>
                                </div>
                              </div>
                              <div className="px-6 py-4">
                                <RadialChart 
                                    percentage={Number(state.delivered_percentage)} 
                                    />  
                              </div>

                            </div>
                            <div className="w-[100%] bg-white rounded-xl shadow-lg transition-all duration-500 hover:shadow-xl">
                              <div className="p-4 border-b border-gray-200">
                                <div className="flex items-center justify-between">
                                  <div>
                                    <h3 className="text-sm font-bold text-gray-900">Pourcentage Confirmé</h3>
                                    <p className="text-[10px] text-gray-600">Indique des commandes confirmées.</p>
                                  </div>
                                </div>
                              </div>
                              <div className="px-6 py-4">
                                <RadialChart 
                                    percentage={Number(state.confirme_percentage)} 
                                    />  
                              </div>
                              </div> 
                          </div>
                          <div className="w-[100%] max-h-max p-1 bg-white rounded-xl shadow-lg transition-all duration-500 hover:shadow-xl">
                            <BaarChart data={state.daily_orders} />
                          </div>

                          <div className="w-[100%] max-h-max p-1 bg-white rounded-xl shadow-lg transition-all duration-500 hover:shadow-xl">
                            <LinneChart data={state.daily_revenue}  />
                          </div>

                        </div>

                        <div className="grid gap-x-6 space-y-6 max-h-max xl:col-span-2 md:grid-cols-2 xs:grid-cols-1">
                          <div className="xl:col-span-2 bg-white rounded-xl shadow-lg max-h-max transition-all duration-500 hover:shadow-xl">
                            <div className="px-6 py-4 border-b border-gray-200">
                              <h3 className="text-sm font-bold text-gray-900">Répartition des commandes par ville</h3>
                              <p className="text-[10px] text-gray-600">Nombre de commandes par zone géographique</p>
                            </div>
                            <div className="">

                              <Map Data={state?.cities_orders}  />
                            </div>
                          </div>

                          <div className="xl:col-span-2 bg-white rounded-xl shadow-lg max-h-max transition-all duration-500 hover:shadow-xl">
                            <div className="px-6 py-4 border-b border-gray-200">
                              <h3 className="text-sm font-bold text-gray-900">Les cinq Paiements</h3>
                              <p className="text-[10px] text-gray-600">Aperçu des cinq paiements</p>
                            </div>
                            <div className="px-6 py-4 space-y-2">
                              {
                                state.last_payments.map((payment,index)=>(
                                  <div key={index} className="shadow-sm bg-gray-50/20 rounded-md flex justify-between items-center py-2 px-3">
                                    <div>
                                      <div className="flex gap-2">
                                        <div className="p-2">
                                          <CheckCircle className="text-green-600 w-4 h-4" />
                                        </div>
                                        <div>
                                          <div className="text-xs">{payment.invoice_number}</div>
                                          <div className="text-[10px] text-gray-500">Versé  le : {payment.payment_date}</div>
                                        </div>
                                      </div>
                                    </div>
                                    <div>
                                      <span className="font-semibold text-green-600 text-xs">+{formatMoney(payment.total)} MAD</span>
                                    </div>
                                  </div>
                                ))
                              }
                            </div>
                          </div>

                          <div className="xl:col-span-2 bg-white rounded-xl shadow-lg min-xl:max-h-max transition-all duration-500 hover:shadow-xl">
                            <div className="px-6 py-4 border-b border-gray-200">
                              <h3 className="text-sm font-bold text-gray-900">Top 5 des produits</h3>
                              <p className="text-xs text-gray-600">Produits les plus performants</p>
                            </div>
                            <div className="px-6 py-4">
                              {
                                state.top_products?.length > 0 ? 
                                  <div className="space-y-3">
                                    {topProducts.map((product, index) => (
                                      <div 
                                        key={product.name + index} 
                                        className={`space-y-2 transition-all duration-500 ${
                                          showContent ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-4'
                                        }`}
                                        style={{
                                          transitionDelay: showContent ? `${index * 100 + 700}ms` : '0ms'
                                        }}
                                      >
                                        <div className="flex items-center justify-between">
                                          <div className="flex items-center space-x-3">
                                            <div className="flex items-center justify-center w-8 h-8 rounded-lg overflow-hidden">
                                              {product.image ? (
                                                <img 
                                                  width="32"
                                                  height="32"
                                                  alt={product.name}
                                                  src={`${BASE_URL}/storage/${product.image}`}
                                                  className="object-cover"
                                                />
                                              ) : (
                                                <div className="w-8 h-8 bg-gray-200 rounded-lg flex items-center justify-center">
                                                  <Package className="w-4 h-4 text-gray-400" />
                                                </div>
                                              )}
                                            </div>
                                            <div>
                                              <p className="font-medium text-gray-900 text-sm">{product.name}</p>
                                              <p className="text-xs text-gray-500">{product.sales} ventes - {product.progress.toFixed(2)}%</p>
                                            </div>
                                          </div>
                                          <div className="text-right">
                                            <p className="font-semibold text-green-600 text-xs">
                                              {showContent ? product.revenue : '0.00 MAD'}
                                            </p>
                                          </div>
                                        </div>
                                        <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                                          <div
                                            className="bg-gradient-to-r from-[var(--color-primary-300)] to-[var(--color-primary-500)] h-2 rounded-full transition-all duration-1000 ease-out"
                                            style={{ 
                                              width: showContent ? `${product.progress}%` : '0%'
                                            }}
                                          />
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                :
                                <div className="h-10"></div>
                              }
                            </div>
                          </div>

                        </div>
                      </div>

                    </div>
                  }
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  )
}
